"use client";

import { useCallback, useRef, useState } from "react";

export interface SwipeActionsRow {
  /** Horizontal translate to apply to the row content (px, <= 0). */
  offset: number;
  /** True when the actions tray is fully revealed. */
  open: boolean;
  /** Spread onto the swipeable row element. */
  handlers: {
    onPointerDown: (e: React.PointerEvent) => void;
    onPointerMove: (e: React.PointerEvent) => void;
    onPointerUp: (e: React.PointerEvent) => void;
    onPointerCancel: (e: React.PointerEvent) => void;
  };
  /** Close the tray programmatically. */
  close: () => void;
  /** Toggle the tray (used by the ⋯ fallback button on web/desktop). */
  toggle: () => void;
}

/**
 * Pointer-events swipe-left hook revealing a row-action tray (proposal 19).
 * Touch: drag left up to `actionsWidth`, snaps open/closed.
 * Mouse (web/desktop): use `toggle()` from a ⋯ button — drag is touch-only.
 */
export function useSwipeActions(actionsWidth = 96): SwipeActionsRow {
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState(false);
  const start = useRef<{ x: number; y: number; base: number } | null>(null);
  const dragging = useRef(false);

  const close = useCallback(() => {
    setOffset(0);
    setOpen(false);
  }, []);

  const toggle = useCallback(() => {
    setOpen((v) => {
      const next = !v;
      setOffset(next ? -actionsWidth : 0);
      return next;
    });
  }, [actionsWidth]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerType !== "touch") return;
      start.current = { x: e.clientX, y: e.clientY, base: open ? -actionsWidth : 0 };
      dragging.current = false;
    },
    [open, actionsWidth]
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!start.current) return;
      const dx = e.clientX - start.current.x;
      const dy = e.clientY - start.current.y;
      if (!dragging.current) {
        if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy)) return;
        dragging.current = true;
      }
      const next = Math.min(0, Math.max(-actionsWidth, start.current.base + dx));
      setOffset(next);
    },
    [actionsWidth]
  );

  const settle = useCallback(
    (e: React.PointerEvent) => {
      if (!start.current) return;
      if (dragging.current) {
        const shouldOpen = offset < -actionsWidth / 2;
        setOpen(shouldOpen);
        setOffset(shouldOpen ? -actionsWidth : 0);
        e.preventDefault();
      }
      start.current = null;
      dragging.current = false;
    },
    [offset, actionsWidth]
  );

  return {
    offset,
    open,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: settle,
      onPointerCancel: settle,
    },
    close,
    toggle,
  };
}

export default useSwipeActions;
